<template>

  <el-form ref='licenseeForm' v-model="telco" label-position="top" :aria-busy="saving ? 'true' : null" @submit.native.prevent>

    <!-- XOL-626: the field block is keyed so Cancel can remount it. The select atoms below
         copy their `value` once in data() (the XOL-552 finding), so restoring `telco` alone
         would leave them showing what the user had typed. -->
    <div :key="formKey">

    <el-row :gutter="24">

      <el-col :span="12">
        <el-form-item label="Sharer">
          <strong>{{sharerName}}</strong>
        </el-form-item>
      </el-col>

      <el-col :span="12">
        <el-form-item label="Telco Site ID" required :error="telcoSiteIdError">
          <el-input v-if="!readMode" ref="telcoSiteIdInput" placeholder="Site ID" v-model="telco.telcoSiteId"
            :disabled="fieldsDisabled" @input="telcoSiteIdError = ''" />
          <b v-else>{{ telco.telcoSiteId || '—' }}</b>
        </el-form-item>
      </el-col>
    </el-row>

    <el-row :gutter="12">
      <el-col :md="24" :lg="12">
        <el-form-item label="Sharer No.">
          <!-- XOL-626 (D16): `undefined`, never null. Element's input-number turns null into
               Number(null) = 0 and clamps it to :min="1" when it mounts, so a null here would
               show (and send) 1. undefined skips the clamp, and the box shows its placeholder. -->
          <b v-if="sharerNoReadOnly && telco.sharerNo">{{ telco.sharerNo }}</b>
          <span v-else-if="sharerNoReadOnly" class="text-regular">Unassigned</span>
          <el-input-number v-else v-model="telco.sharerNo" class="sharer-no-input" :min="1" :max="99" :step="1"
            controls-position="right" size="small" :placeholder="mode === 'modify' ? 'Unassigned' : 'Auto'"
            :disabled="sharerNoDisabled" />
          <div v-for="hint in sharerNoHints" :key="hint" class="sharer-no-hint text-regular">{{hint}}</div>
        </el-form-item>
      </el-col>
    </el-row>

    <!-- XOL-626 (D12): the Sharer No. banner. Its own row AFTER the Sharer No. row and outside
         every field's row, so showing it only moves the later rows down as a block (the input
         being edited never moves; ProjectTab.vue:12-17 rule). The duplicate / #0 rules are
         src/lib/sharerNoNotice.js, shared with the Project tab. -->
    <el-row v-if="sharerNoBanner" :gutter="12">
      <el-col :md="24">
        <div :class="['sharer-no-banner', 'sharer-no-banner--' + sharerNoBanner.kind]">
          <el-alert v-if="sharerNoBanner.kind === 'alert'"
            type="warning" :closable="false" show-icon role="alert"
            :title="sharerNoBanner.title" :description="sharerNoBanner.description" />

          <div v-else-if="sharerNoBanner.kind === 'note'" class="sharer-no-note" role="status">
            <i class="fa fa-exclamation-circle sharer-no-note__icon" aria-hidden="true"></i>
            <div class="sharer-no-note__body">
              <span class="sharer-no-note__lead">{{ sharerNoBanner.lead }}<el-tooltip
                  v-if="sharerNoBanner.peersTooltip"
                  :content="sharerNoBanner.peersTooltip"
                  placement="top"><i class="fa fa-info-circle sharer-no-note__info"
                    role="img" :aria-label="sharerNoBanner.peersTooltip"></i></el-tooltip></span>
              <span class="sharer-no-note__detail">{{ sharerNoBanner.detail }}</span>
            </div>
          </div>

          <span v-else class="sharer-no-hint text-regular">{{ sharerNoBanner.text }}</span>
        </div>
      </el-col>
    </el-row>

    <el-row :gutter="12">
      <el-col :md="24" :lg="12">
        <el-form-item v-if="readMode" label="Service Type">
          <b>{{ telco.serviceType || '—' }}</b>
        </el-form-item>
        <select-service-type v-else v-model="telco.serviceType" :disabled="fieldsDisabled" />
      </el-col>

      <el-col  :md="24" :lg="12">
        <el-form-item v-if="readMode" label="Spectrum">
          <b>{{ telco.spectrumBand || '—' }}</b>
        </el-form-item>
        <select-spectrum v-else v-model="telco.spectrumBand" :disabled="fieldsDisabled" />
      </el-col>
    </el-row>

    <!-- Create only (XOL-626 S5): in Modify the rate lives in "Sharer Finance", which owns it. -->
    <el-row v-if="mode === 'create'" :gutter="12">
      <el-col :md="24">
        <sharer-rate-override v-if="canManageRates"
          v-model="telco.rate_id"
          :sharer-id="sharerId"
          :disabled="fieldsDisabled" />

        <!-- XOL-626 (D19): a non-manager picks the rate from the request-scoped list (the by-org
             list is manager-only). A plain select: Duplicate / Modify create or change rates, so
             they stay with the managers. After Create the rate is changed in Sharer Finance. -->
        <template v-else>
          <select-sharer-rates
            :key="'rate-' + rateSelectKey"
            ref="rateSelect"
            v-model="telco.rate_id"
            label="Sharer Rate"
            placeholder="Select a rate (optional)"
            :sharer-id="sharerId"
            :options-path="rateOptionsUrl"
            :disabled="fieldsDisabled"
            clearable
            no-data-text="No rates for this sharer" />
          <div class="sharer-rate-hint sharer-rate-hint--select text-regular">After the sharer is created, only a finance, admin or management manager, or a super user, can change the rate.</div>
        </template>
      </el-col>
    </el-row>

    <!-- XOL-467: any-to-all auto-fill notice. Source-agnostic copy — any of
         Handover/Agreement Start/Billing Start can be the field the user
         actually typed. -->
    <el-row v-if="autoFilled" :gutter="12">
      <el-col :md="24">
        <el-alert type="info" show-icon :closable="false"
          description="Any dates that were still blank have been filled in from the date you just set. Adjust them if they differ." />
      </el-col>
    </el-row>

    <el-row :gutter="12">
      <el-col :md="24" :lg="12">
        <el-form-item label='Handover Date' required :class="{'licensee-date--editing': !readMode}" :error="fieldErrors.handover">
          <el-date-picker v-if="!readMode" ref="dateField_handover" value-format="yyyy-MM-dd" :format="format"
          v-model="telco.handover"  :disabled="fieldsDisabled" @change="onDateChange('handover', $event)" />
          <b v-else>{{ telco.handover ? formatDate(telco.handover) : '—' }}</b>
        </el-form-item>
      </el-col>

      <el-col :md="24" :lg="12">
        <el-form-item label='Agreement Start' required :class="{'licensee-date--editing': !readMode}" :error="fieldErrors.started">
          <el-date-picker v-if="!readMode" ref="dateField_started" value-format="yyyy-MM-dd" :format="format"
          v-model="telco.started"  :disabled="fieldsDisabled" @change="onDateChange('started', $event)" />
          <b v-else>{{ telco.started ? formatDate(telco.started) : '—' }}</b>
        </el-form-item>
      </el-col>

      <!-- XOL-467: Billing Start was never collected here, so a new sharer had no
           way to say when billing begins. Left blank the API derives it — from
           the commencement date, or from the handover when that is blank too —
           but deriving it is the fallback, not the intent. -->
      <el-col :md="24" :lg="12">
        <el-form-item label='Billing Start' :class="{'licensee-date--editing': !readMode}" :error="fieldErrors.startBilling">
          <el-date-picker v-if="!readMode" ref="dateField_startBilling" value-format="yyyy-MM-dd" :format="format"
          v-model="telco.startBilling"  :disabled="fieldsDisabled" @change="onDateChange('startBilling', $event)" />
          <b v-else>{{ telco.startBilling ? formatDate(telco.startBilling) : '—' }}</b>
        </el-form-item>
      </el-col>

      <el-col :md="24" :lg="12">
        <el-form-item label='On Air Date' :class="{'licensee-date--editing': !readMode}">
          <el-date-picker v-if="!readMode" value-format="yyyy-MM-dd" :format="format"
          v-model="telco.onAir"  :disabled="fieldsDisabled" @change="clearDateErrors" />
          <b v-else>{{ telco.onAir ? formatDate(telco.onAir) : '—' }}</b>
        </el-form-item>
      </el-col>

      <el-col :md="24" :lg="12">
        <el-form-item label='Expiry Date' :class="{'licensee-date--editing': !readMode}" :error="fieldErrors.expiry">
          <el-date-picker v-if="mode === 'create'" ref="dateField_expiry" value-format="yyyy-MM-dd" :format="format"
          label='Contract End' :value="expiryDate" disabled />
          <b v-else>{{ expiryDate ? formatDate(expiryDate) : '—' }}</b>
        </el-form-item>
      </el-col>
    </el-row>

    <!-- XOL-67 follow-up (decisions 193 to 202): the site handover block. Its own row after the sharer's dates, inside the keyed div so Cancel
         remounts it; shown only for an RTS that an RTA opened (see `showTransition`). A refusal puts its alert in the same slot. -->
    <el-row v-if="showTransition || showTransitionRefusal" :gutter="12">
      <el-col :span="24">
        <site-transition-fields
          ref="siteTransition"
          :value="transition"
          :mode="mode"
          :site="site"
          :sharer-handover="telco.handover"
          :errors="transitionErrors"
          :disabled="fieldsDisabled"
          :refusal="showTransitionRefusal ? transitionRefusal : null"
          @change="transitionErrors = {}" />
      </el-col>
    </el-row>

    <el-row :gutter="8">
      <el-col :span="12">
        <el-form-item prop="capex" label="CAPEX">
          <el-input v-if="!readMode" type="number" placeholder="CAPEX"
          v-model="telco.capex"  :disabled="fieldsDisabled"/>
          <b v-else>{{ moneyOrDash(telco.capex) }}</b>
        </el-form-item>
      </el-col>

      <el-col :span="12">
        <el-form-item label="OPEX">
          <el-input v-if="!readMode" type="number" placeholder="OPEX" v-model="telco.opex"
          :disabled="fieldsDisabled" />
          <b v-else>{{ moneyOrDash(telco.opex) }}</b>
        </el-form-item>
      </el-col>
    </el-row>


    <!-- Create only: Cabins and Own Meter are `assets` rows, not licensee columns, and saving
         them again from a Modify would POST duplicate assets. -->
    <el-row v-if="mode === 'create'" :gutter="8">
      <el-col :span="12">
        <el-form-item>
          Cabins &nbsp;<el-input-number v-model="telco.cabin" :min="0" :max="5" size="mini" :disabled="fieldsDisabled" />
        </el-form-item>
      </el-col>
      <el-col :span="12">
        <check-box v-model="telco.ownMeter"  :disabled="fieldsDisabled">{{'Own  ' + utilLabel + ' Meter'}}</check-box>
      </el-col>
    </el-row>

    <el-form-item label='Tenor (years)'>
      <el-slider v-if="!readMode"
      v-model="tenor" :min="1" :max="20" show-stops show-input  :disabled="fieldsDisabled" @change="onTenorChange">
    </el-slider>
    <b v-else>{{ tenor }} {{ tenor === 1 ? 'year' : 'years' }}</b>
    <div v-if="tenorHint" class="sharer-rate-hint text-regular">{{tenorHint}}</div>
  </el-form-item>

    </div>

    <el-row>
      <el-col align="middle">
        <el-button class="btn-icon-gap" v-if="mode === 'create'" ref="saveButton" type="success" icon="fa fa-check" size="mini" round :loading="saving || busy"
          :disabled="readOnly" @click="onSave">Save</el-button>

        <template v-else>
          <template v-if="!editing">
            <el-button class="btn-icon-gap" v-if="!readOnly" ref="editButton" type="info" icon="fa fa-edit" size="mini" round
              :disabled="editBlocked !== null" :aria-describedby="editBlocked ? editBlockedId : null"
              @click="startEdit">Edit</el-button>
            <span v-if="!readOnly && editBlocked" :id="editBlockedId" class="edit-blocked-hint text-regular">{{editBlocked}}</span>
          </template>

          <template v-else>
            <el-button class="btn-icon-gap" ref="cancelButton" type="warning" icon="fa fa-times-circle" size="mini" round :disabled="saving" @click="cancelEdit">Cancel</el-button>
            <el-button class="btn-icon-gap" v-if="!readOnly" ref="saveButton" type="success" icon="fa fa-check" size="mini" round :loading="saving"
              @click="onSave">Save</el-button>
          </template>
        </template>
      </el-col>
    </el-row>

  </el-form>

</template>


<script>

import {settings} from '@/components/Data'

import PostMixins from '@/mixins/PostMixins'
import DateMixins from '@/mixins/DateMixins'
import NumberMixins from '@/mixins/NumberMixins'
import DateChainMixin from '@/mixins/DateChainMixin'
import DrawerDirtyMixin from '@/mixins/DrawerDirtyMixin'
import {dateChainErrors, autoFillDates} from '@/lib/licenseeDates'
import {toAppTime, appNowDateSql} from '@/lib/appTime'
import {
  REFUSAL_CODES,
  offersTransition,
  otherLiveSharers,
  initialState as transitionInitial,
  follow as transitionFollow,
  sameAsSite,
  errors as transitionCheck,
  payload as transitionPayload,
  serverErrors as transitionServerErrors,
  refusalCopy,
  successSentence
} from '@/lib/siteTransition'
import {
  LINKED_SHARER_BASE,
  NOT_FOUND,
  NOT_LINKED,
  SHARER_NO_FORBIDDEN,
  INVALID_VALUE,
  SHARER_TERMINATED,
  TELCO_SITE_ID_IN_USE,
  errorCode,
  errorMessage,
  rateOptionsPath
} from '@/lib/requestSharer'
import {
  LABEL_STEP,
  SHARER_NO_EDITORS,
  confirmHtmlFor,
  confirmTitleFor,
  noteFor,
  noticeFor,
  sharerNoDrivesFee
} from '@/lib/sharerNoNotice'

import SelectServiceType from '@/components/atoms/selects/SelectServiceType'
import SelectSpectrum from '@/components/atoms/selects/SelectSpectrum'
import SelectSharerRates from '@/components/atoms/selects/SelectSharerRates'
import SharerRateOverride from '@/components/finance/rates/SharerRateOverride'
import SiteTransitionFields from '@/components/sites/SiteTransitionFields'

// XOL-467 (any-to-all widening): the three fields this form can both source
// and target auto-fill from/to. See licenseeDates.js's autoFillDates() doc.
var DATE_CHAIN_FIELDS = ['handover', 'started', 'startBilling']

// The four dates the XOL-467 chain judges. Each is sent on a Modify only when it differs
// from its baseline (the dirty-gate that keeps ~72 chain-breaking Active rows saveable).
var CHAIN_DATES = ['handover', 'started', 'startBilling', 'expiry']

// The tenor the slider falls back to, and its bounds (the slider's :min / :max).
var DEFAULT_TENOR = 10
var TENOR_MIN = 1
var TENOR_MAX = 20

var TENOR_HINT_LEAD = "Expiry Date isn't a whole number of years after Agreement Start."
var TENOR_HINT_EDIT = ' Changing Tenor or Agreement Start recalculates it. To set another date, use Site → Finance → Sharers.'

// A date as the wire string, or null. formatDateSql returns null for anything unparseable;
// the explicit sentinels are the ones the date picker and the API produce.
function dateOf (self, v) {
  if (v === null || v === undefined || v === '' || v === 'invalid date' || v === '0000-00-00') return null

  return self.formatDateSql(v)
}

function textOf (v) {
  return (v === null || v === undefined) ? '' : String(v)
}

// A text column restored from its baseline: '' means the row had none (null), so the input
// and the select atoms get null back, not an empty string.
function nullIfEmpty (text) {
  return text === '' ? null : text
}

function numberOf (v) {
  if (v === null || v === undefined || v === '') return null

  var n = Number(v)

  return isNaN(n) ? null : n
}

// null, 0 and undefined all mean "unassigned" for the dirty-gate; otherwise the integer.
function sharerNoOf (v) {
  if (v === null || v === undefined || v === '') return 0

  var n = Number(v)

  return (isNaN(n) || !isFinite(n)) ? 0 : Math.trunc(n)
}

export default {
  mixins: [DateMixins, NumberMixins, PostMixins, DateChainMixin, DrawerDirtyMixin],

  components: {
    SelectServiceType,
    SelectSharerRates,
    SelectSpectrum,
    SharerRateOverride,
    SiteTransitionFields
  },

  props: {
    siteId: {
      type: [String, Number],
      required: true
    },

    sharerName: {
      type: String,
      required: true
    },

    sharerId: {
      type: Number,
      required: true
    },

    // XOL-626: the sharer this request created (the decorated row from
    // GET /licensees/action/{actionId}). Set => Modify mode; null => Create mode.
    licensee: {
      type: Object,
      default: null
    },

    // The REQUEST (action) id: the Modify PUT is PUT /licensees/action/{actionId}.
    actionId: {
      type: Number,
      default: null
    },

    // Frozen (step 3 complete) or Terminated: no Edit, no Save. Set from step 3.
    readOnly: {
      type: Boolean,
      default: false
    },

    // XOL-626 (UX §4.7, one editor at a time): why Edit is disabled. null = not blocked,
    // '' = blocked with no hint (the re-read after a save is in flight), else the hint text.
    editBlocked: {
      type: String,
      default: null
    },

    // XOL-626 (UX §5.4): step 3 is re-reading the request's sharer after a failed Create.
    // Save keeps its spinner until the lookup answers.
    busy: {
      type: Boolean,
      default: false
    },

    // Move focus to Edit once mounted (the Create -> Modify flip, UX §2).
    focusEditOnMount: {
      type: Boolean,
      default: false
    },

    // XOL-596 (completed steps): Create Sharer is used from RTS step 3 (party 1): that
    // row's `action_steps.id` is sent with the create and the modify so the API refuses
    // them while the step is complete (nothing is written).
    actionStepId: {
      type: Number,
      default: null
    },

    sharerSiteId: {
      type: String,
      default: null
    },

    serviceType: {
      type: String,
      default: null
    },

    spectrumBand: {
      type: String,
      default: null
    },

    equipment: {
      type: Array,
      default: function () { return [] }
    },

    // XOL-67 follow-up (decision 200): the request row's site (`rowData.site`: status, siteName, handover, handoverStatus), and whether the
    // RTS was opened by an RTA's Complete (`rowData.rta_id`). Both, plus a Candidate site and no other live sharer, show the site handover
    // block. A null site or `fromRta` false shows nothing, and the form is exactly as it was.
    site: {
      type: Object,
      default: null
    },

    fromRta: {
      type: Boolean,
      default: false
    }
  },

  methods: {
    // Pushes posts to the server when called.
    resetData () {
      this.telco = this.getTableStruct('licensee')
      this.telco.site_id = this.siteId
      this.autoFilled = false
    },

    // -- Modify: the loaded row, its baselines, the tenor ---------------------------------

    // The editable fields of the loaded licensee, normalised the SAME way the live values are
    // compared (dateOf / textOf / numberOf / sharerNoOf). Dates MUST be normalised here too,
    // or chainFieldsDirty reads every untouched date as changed and the ~72 chain-breaking
    // Active rows could not save a Telco Site ID change (XOL-467).
    baselineOf (lic) {
      return {
        telcoSiteId: textOf(lic.telcoSiteId),
        serviceType: textOf(lic.serviceType),
        spectrumBand: textOf(lic.spectrumBand),
        onAir: dateOf(this, lic.onAir),
        capex: numberOf(lic.capex),
        opex: numberOf(lic.opex),
        handover: dateOf(this, lic.handover),
        started: dateOf(this, lic.started),
        startBilling: dateOf(this, lic.startBilling),
        expiry: dateOf(this, lic.expiry),
        sharerNo: sharerNoOf(lic.sharerNo)
      }
    },

    // The model for the inputs, from the loaded licensee. A stored 0 or null Sharer No.
    // shows as `undefined` (D16 / UX §3.2): an empty box with the placeholder "Unassigned".
    telcoOf (lic) {
      var base = this.baselineOf(lic)

      return {
        id: lic.id,
        site_id: lic.site_id,
        organisation_id: lic.organisation_id,
        status: lic.status,
        telcoSiteId: lic.telcoSiteId,
        serviceType: lic.serviceType,
        spectrumBand: lic.spectrumBand,
        sharerNo: base.sharerNo > 0 ? base.sharerNo : undefined,
        handover: base.handover,
        started: base.started,
        startBilling: base.startBilling,
        onAir: base.onAir,
        expiry: base.expiry,
        capex: lic.capex,
        opex: lic.opex
      }
    },

    // The whole years between Agreement Start and Expiry (clamped to the slider), or the
    // default when either is missing.
    tenorOf (started, expiry) {
      var s = toAppTime(started)
      var e = toAppTime(expiry)

      if (s === null || e === null) return DEFAULT_TENOR

      var years = Math.round(e.diff(s, 'years', true))

      return Math.min(TENOR_MAX, Math.max(TENOR_MIN, years))
    },

    // -- Edit / Cancel (Modify) -----------------------------------------------------------

    // A read-mode amount: a blank (null / '') is a dash, not RM0.00 (formatMoney(null) is 0); a real 0 stays RM0.00.
    moneyOrDash (v) {
      return (v === null || v === undefined || v === '') ? '—' : this.formatMoney(v)
    },

    startEdit () {
      if (this.mode !== 'modify' || this.readOnly) return

      this.editing = true
      this.fieldErrors = {}
      this.telcoSiteIdError = ''
      this.autoFilled = false

      // The site handover block starts unticked every time edit mode is entered.
      this.resetTransition()

      var self = this
      this.$nextTick(function () {
        self.focusTelcoSiteId(false)
      })
    },

    // No confirm (same as EditPane and ProjectTab): restore what was loaded or last saved.
    cancelEdit () {
      var b = this.baseline

      this.telco.telcoSiteId = nullIfEmpty(b.telcoSiteId)
      this.telco.serviceType = nullIfEmpty(b.serviceType)
      this.telco.spectrumBand = nullIfEmpty(b.spectrumBand)
      this.telco.onAir = b.onAir
      this.telco.capex = b.capex
      this.telco.opex = b.opex
      this.telco.handover = b.handover
      this.telco.started = b.started
      this.telco.startBilling = b.startBilling
      this.telco.expiry = b.expiry
      this.telco.sharerNo = b.sharerNo > 0 ? b.sharerNo : undefined
      this.tenor = this.tenorOf(b.started, b.expiry)

      this.fieldErrors = {}
      this.telcoSiteIdError = ''
      this.autoFilled = false

      // Not `transitionGone`: a refusal lasts for the form's life (UX A3).
      this.resetTransition()
      this.formKey++
      this.editing = false

      this.requestEditFocus()
    },

    // The baseline of a Create: nothing loaded.
    blankBaseline () {
      return {
        telcoSiteId: '',
        serviceType: '',
        spectrumBand: '',
        onAir: null,
        capex: null,
        opex: null,
        handover: null,
        started: null,
        startBilling: null,
        expiry: null,
        sharerNo: 0
      }
    },

    // Back in read mode (Cancel, a no-op Save, a saved Save, the flip): focus goes to Edit, as soon as
    // it exists and is enabled (right after a save step 3 re-reads, and Edit is disabled meanwhile).
    requestEditFocus () {
      var self = this

      this.wantEditFocus = true
      this.$nextTick(function () { self.tryFocusEditOnMount() })
    },

    // The flip's focus request: Edit is disabled for a moment after the flip (step 3 is re-reading
    // the sharer, `editBlocked` is ''), and a disabled button cannot take focus. So the request is
    // kept until Edit is enabled (see the editBlocked watcher).
    tryFocusEditOnMount () {
      if (!this.wantEditFocus) return

      var b = this.$refs.editButton
      var el = b && (b.$el || b)

      if (!el || el.disabled) return

      this.wantEditFocus = false
      el.focus()
    },

    focusSave () {
      var b = this.$refs.saveButton
      var el = b && (b.$el || b)

      if (el && el.focus) el.focus()
    },

    focusCancel () {
      var b = this.$refs.cancelButton
      var el = b && (b.$el || b)

      if (el && el.focus) el.focus()
    },

    // A text input: focusing it opens nothing (unlike the date pickers, XOL-479).
    focusTelcoSiteId (scroll) {
      var input = this.$refs.telcoSiteIdInput

      if (!input) return

      if (scroll && input.$el && input.$el.scrollIntoView) {
        input.$el.scrollIntoView({ block: 'center' })
      }

      if (input.focus) input.focus()
    },

    // XOL-416 (was XOL-376 Inc 4): prefill the Sharer No. from the server-suggested
    // next free slot. {suggested:N} comes back as raw JSON, no data wrapper.
    // XOL-626 (D16): `undefined`, not null, so the box shows "Auto" instead of being clamped to 1.
    applyBillingSuggestion (data) {
      this.telco.sharerNo = (data && data.suggested) ? data.suggested : undefined
    },

    // fetchData already toasts on failure — no extra message here. Leaving it
    // blank is safe: the server assigns MAX(non-Terminated)+1 when the key is omitted,
    // which is the same number the suggestion endpoint would have returned.
    onSuggestionFail () {
      this.telco.sharerNo = undefined
    },

    // -- D12: the data the Sharer No. notices need ----------------------------------------

    // The site's sharers, for the duplicate check. A failed GET toasts (fetchData) and
    // leaves []: never infer "no duplicate" from a missing list.
    //
    // XOL-67 (decision 199): the same list says whether the site has ANOTHER sharer, and there a missing list must hide the block, not
    // show it. `peersState` is 'loaded' only for a real array; 'failed' otherwise. The notices above keep their own behaviour.
    loadPeers () {
      if (!this.siteId) return

      var self = this
      this.fetchData(
        'licensees/site/' + this.siteId + '?per_page=200',
        function (data) {
          self.peers = Array.isArray(data) ? data : []
          self.peersState = Array.isArray(data) ? 'loaded' : 'failed'
        },
        'site sharers',
        function () {
          self.peers = []
          self.peersState = 'failed'
        }
      )
    },

    // -- XOL-67 follow-up: the site handover block ----------------------------------------

    // The block's state from the CURRENT site and sharer date (the site arrives with the request row and can change under us).
    resetTransition () {
      this.transition = transitionInitial(this.site, this.mode, this.telco ? this.telco.handover : null)
      this.transitionErrors = {}
    },

    // The messages of the block at Save, or {} when nothing is to be sent. The `follow()` is a safety net: the block's `sharerHandover`
    // watcher already keeps the site date in step with `telco.handover`, but the caller has just normalised it, and a stale date must never be sent.
    transitionCheckNow () {
      if (!this.transitionSends) return {}

      transitionFollow(this.transition, dateOf(this, this.telco.handover))

      return transitionCheck(this.transition)
    },

    // ONE toast for a blocked save, whoever blocked it (UX G2): the date chain's fields plus the block's. With a block field in the count
    // the noun is "fields" (a missing status is not a date); the date-chain-only wording is untouched. Focus: the first chain field (it is
    // above the block); else the caller's own (`keepFocus`, Modify's Telco Site ID); else the block's group, never one of its pickers.
    reportBlocked (chainFields, blockErrors, keepFocus) {
      var blockCount = Object.keys(blockErrors).length
      var count = chainFields.length + blockCount

      this.transitionErrors = blockErrors

      if (count === 0) return false

      if (blockCount > 0) {
        this.notifyBlockedSave(count, 'field')
      } else {
        this.notifyBlockedSave(count)
      }

      if (chainFields.length > 0) {
        this.focusDateField(chainFields[0])
      } else if (blockCount > 0 && !keepFocus) {
        this.focusTransitionGroup()
      }

      return true
    },

    focusTransitionGroup () {
      var self = this

      this.$nextTick(function () {
        var block = self.$refs.siteTransition

        if (block && typeof block.focusGroup === 'function') block.focusGroup()
      })
    },

    // Deliberate: step 3's `rowData.site` (the `site` prop) is updated IN PLACE after a save that sent the block, so the block and the next Edit
    // (and the Modify form that replaces a Create) read the values just saved, with no second read of the request row. The values come from the
    // answer's `site`, else from what was sent.
    applySiteTransition (answered, sent) {
      if (!sent || !this.site) return

      var a = (answered && typeof answered === 'object') ? answered : {}

      this.$set(this.site, 'handover', a.handover !== undefined ? a.handover : sent.handover)
      this.$set(this.site, 'handoverStatus', a.handoverStatus !== undefined ? a.handoverStatus : sent.handoverStatus)
    },

    // The API refused the block (the whole save, so nothing was created or changed): the alert takes the block's slot for the form's
    // life, every value stays, and Save is focused. The next Save sends no `siteTransition`.
    refuseTransition (code) {
      var copy = refusalCopy(code, this.mode, this.site ? this.site.siteName : '')

      this.transitionGone = true
      this.transitionRefusal = copy
      this.sentTransition = null
      this.transitionErrors = {}

      // site_transition_not_rta has no message of ours: PostMixins already toasted that bug signal.
      if (copy && copy.notify) {
        this.$notify({
          title: 'Not saved',
          message: copy.notify,
          type: 'warning',
          duration: 5500
        })
      }

      var self = this
      this.$nextTick(function () { self.focusSave() })
    },

    // A failed write that the block explains: a refusal code, or a 422 on its own fields. True when handled (the caller returns).
    transitionFailure (errOrErrors) {
      var code = errorCode(errOrErrors)

      if (REFUSAL_CODES.indexOf(code) !== -1) {
        this.refuseTransition(code)
        return true
      }

      var fields = transitionServerErrors(errOrErrors)

      if (Object.keys(fields).length > 0 && this.transitionSends) {
        this.transitionErrors = fields
        this.focusTransitionGroup()
        return true
      }

      return false
    },

    // Modify only (a Create has no licensee yet, so its billing mode stays null = "the number
    // matters", which errs toward warning). Failure is non-fatal for the same reason.
    loadBillingMode () {
      if (this.billingMode || !this.telco.id) return

      var self = this
      this.fetchDataRawURL(
        this.getEndpoint('/licensees/' + this.telco.id + '/fee-schedule'),
        function (data) {
          var meta = data && data.meta
          if (meta && meta.billingMode) self.billingMode = String(meta.billingMode)
        },
        'billing mode',
        function () { /* leave null — assume the Sharer No. matters */ }
      )
    },

    // XOL-467: field -> message for `dateChainErrors()`. Every chain field
    // has an input on this form, so nothing needs rerouting.
    dateChainCopy (item) {
      switch (item.code) {
        case 'handover_required':
          return {field: 'handover', message: 'Required when Agreement Start is set.'}
        case 'started_required':
          return {field: 'started', message: 'Required when Billing Start is set.'}
        case 'billing_start_required':
          return {field: 'startBilling', message: 'Required when Agreement Start is set.'}
        case 'billing_before_started':
          return {
            field: 'startBilling',
            message: 'Cannot be before Agreement Start (' + this.formatDate(this.telco.started) + ').'
          }
        case 'expiry_before_billing_start':
          return {field: 'expiry', message: 'Must be after Billing Start.'}
        default:
          return null
      }
    },

    // XOL-467: not wrapped in a Popup, so it needs its own gate — there is
    // no `beforeSave` prop to hook here.
    //
    // XOL-67: `blockErrors` are the site handover block's messages, counted into the same one toast (reportBlocked).
    // `keepFocus` (Modify: the Telco Site ID is itself in error) keeps focus off the block's group, so the order is: first chain field, else the
    // Telco Site ID, else the group.
    validateDates (blockErrors, keepFocus) {
      var self = this
      // XOL-483: Create judges the row the API's create-time cascade produces, not the raw
      // form values (see licenseeDates.js's own docblock). XOL-626: a Modify is a PUT, so it
      // judges the merged row as it stands (`false`).
      var errors = dateChainErrors(this.telco, this.mode === 'create')
      var map = {}

      errors.forEach(function (item) {
        var copy = self.dateChainCopy(item)
        if (copy && !map[copy.field]) {
          map[copy.field] = copy.message
        }
      })

      this.fieldErrors = map

      return !this.reportBlocked(Object.keys(map), blockErrors || {}, keepFocus)
    },

    // Errors clear on ANY date change — the whole map, since the rules are
    // cross-field. Every chain field's change also checks for any-to-all
    // auto-fill (XOL-467), and Agreement Start also rewrites Expiry (XOL-626).
    onDateChange (field, value) {
      this.clearDateErrors()
      this.maybeAutoFillDates(field, value)

      if (field === 'started') {
        this.recomputeExpiry()
      }
    },

    // XOL-626: Expiry used to be a COMPUTED that wrote `telco.expiry` on every evaluation,
    // which would have overwritten a stored expiry with started + 10 years on mount. It is a
    // pure read now, and Tenor / Agreement Start write it explicitly, here.
    recomputeExpiry () {
      this.telco.expiry = this.contractExpiryDate(this.telco.started, this.tenor, 'years', 'YYYY-MM-DD')
    },

    onTenorChange () {
      this.recomputeExpiry()
    },

    // XOL-467 (any-to-all widening): any of Handover/Agreement Start/Billing
    // Start can be the source. Fills each of the other two that is still empty; a value you typed is never a target
    // before this edit — the same gate doubles as the "do not fight the
    // user" re-arm condition (see autoFillDates()'s own doc in
    // licenseeDates.js). Never fires while the inputs are disabled (read-only, saving, or a
    // Modify that is not being edited).
    maybeAutoFillDates (field, value) {
      if (this.fieldsDisabled) return

      var updates = autoFillDates(this.telco, field, value, DATE_CHAIN_FIELDS)
      var keys = Object.keys(updates)
      if (keys.length === 0) return

      var self = this
      keys.forEach(function (key) { self.telco[key] = updates[key] })
      this.autoFilled = true

      // A fill of Agreement Start moves Expiry with it, in a Create always, and in a Modify
      // only when the row had no Expiry (a stored one is never overwritten by a fill).
      if (updates.started !== undefined && (this.mode === 'create' || !this.telco.expiry)) {
        this.recomputeExpiry()
      }
    },

    // -- Save ----------------------------------------------------------------------------

    // The pre-save confirm body when the duplicate warning is showing, else null. Create and
    // Modify both confirm whenever the duplicate alert is active ("save anyway / keep editing").
    duplicateConfirmBody () {
      var banner = this.sharerNoBanner

      if (!banner || banner.kind !== 'alert') return null

      return confirmHtmlFor({
        n: this.telco.sharerNo,
        peers: this.peers,
        selfId: this.mode === 'modify' ? this.telco.id : null,
        billingMode: this.mode === 'modify' ? this.billingMode : null,
        label: LABEL_STEP,
        creatorCannotEdit: this.mode === 'create' && !this.canEditSharerNo
      })
    },

    // Keep editing stays in the form with the values; Save anyway runs `go`.
    confirmThenRun (go) {
      var body = this.duplicateConfirmBody()

      if (!body) return go()

      this.$confirm(body, confirmTitleFor(LABEL_STEP), {
        confirmButtonText: 'Save anyway',
        cancelButtonText: 'Keep editing',
        type: 'warning',
        center: true,
        dangerouslyUseHTMLString: true
      }).then(function () {
        go()
      }).catch(function () {
        // Keep editing: stay in the form with the typed values intact.
      })
    },

    onSave () {
      if (this.mode === 'modify') {
        return this.onSaveModify()
      }

      this.telco.site_id = this.siteId
      this.telco.organisation_id = this.sharerId

      // XOL-416: omit `sharerNo` unless it is a valid 1-99 int, so the server
      // assigns MAX(non-Terminated)+1 itself. The meta struct seeds numeric fields to 0
      // and a failed/skipped prefill leaves null/undefined — sending either would be wrong
      // here: 0 is the deliberate UNASSIGNED sentinel and must never be set by
      // accident at create time, and null is a no-op the server would ignore.
      var n = parseInt(this.telco.sharerNo, 10)
      if (!(n >= 1 && n <= 99)) {
        delete this.telco.sharerNo
      } else {
        this.telco.sharerNo = n
      }

      if (this.telco.started === 'invalid date') {
        this.telco.started = null
      }

      if (this.telco.expiry === 'invalid date') {
        this.telco.expiry = null
      }

      this.telco.outdoor = !this.telco.cabin

      this.telco.started = this.formatDateSql(this.telco.started)
      this.telco.expiry = this.formatDateSql(this.telco.expiry)

      this.telco.handover = this.formatDateSql(this.telco.handover)
      this.telco.startBilling = this.formatDateSql(this.telco.startBilling)
      this.telco.onAir = this.formatDateSql(this.telco.onAir)

      // XOL-467: blocking, field-level — no PUT/POST when the resulting row
      // would violate the chain. XOL-67: the site handover block is judged in the same pass (one toast).
      if (!this.validateDates(this.transitionCheckNow())) return

      // XOL-67: the block rides as `siteTransition` (both keys, the date explicit), or not at all. The API strips it from the licensee.
      this.sentTransition = this.transitionSends ? transitionPayload(this.transition, this.formatDateSql, this.site, 'create') : null

      if (this.sentTransition) {
        this.telco.siteTransition = this.sentTransition
      } else {
        delete this.telco.siteTransition
      }

      if (this.telco && this.equipment && this.equipment.length > 0) {
        this.telco.equipment = this.equipment
      }

      // The step context rides in the body (XOL-596): a refusal (403 step_completed) creates nothing.
      if (this.actionStepId) {
        this.telco.actionStepId = this.actionStepId
      }

      var self = this

      this.confirmThenRun(function () {
        self.saving = true

        // XOL-605: notifyFailureCallback = true. A 500 `{error}` (retried by axios-retry first),
        // a 409 `already_linked`, a 418 and a non-step 403 all land in send()'s branches that only
        // call `failed` when this is on; without it `saving` never cleared and the form never
        // told step 3 to look again. showMessage = false: the success message is ours.
        self.saveForm(self, '/licensees', self.telco.id, self.telco, 'Sharer', self.success, self.failed, false, null, true)
      })
    },

    // The PUT /licensees/action/{actionId} body: EXACTLY the keys the API accepts. Any other key
    // is a 422 `read_only_fields`, so this is built as a NEW object and never from `telco` (which
    // carries status, rate_id, cabin, outdoor, id ... from getTableStruct).
    //
    //   always:  actionStepId, site_id, organisation_id, telcoSiteId, serviceType, spectrumBand,
    //            onAir, capex, opex
    //   dirty:   each of handover / started / startBilling / expiry ONLY when it differs from its
    //            baseline (XOL-467; sending a stored date back arms the row-level chain check)
    //   D6:      sharerNo, only when the user may change it, it changed, and it is an int 1-99
    //   XOL-67:  siteTransition { handover, handoverStatus }, only when the site handover block is ticked and differs from the site
    //
    // Every date, `onAir` included, is a STRING or null (formatDateSql): the API refuses a number.
    buildModifyPayload () {
      var self = this
      var payload = {
        actionStepId: this.actionStepId,
        site_id: this.siteId,
        organisation_id: this.sharerId,
        telcoSiteId: this.telco.telcoSiteId,
        serviceType: this.telco.serviceType,
        spectrumBand: this.telco.spectrumBand,
        onAir: dateOf(this, this.telco.onAir),
        capex: this.telco.capex,
        opex: this.telco.opex
      }

      CHAIN_DATES.forEach(function (key) {
        var now = dateOf(self, self.telco[key])

        if (now !== self.baseline[key]) {
          payload[key] = now
        }
      })

      var n = parseInt(this.telco.sharerNo, 10)

      if (this.canEditSharerNo && n >= 1 && n <= 99 && n !== this.baseline.sharerNo) {
        payload.sharerNo = n
      }

      // XOL-67 (decision 197): the site handover, only when ticked and different from the site's own. No `expected*` keys: the last save wins.
      var site = this.transitionSends ? transitionPayload(this.transition, this.formatDateSql, this.site, 'modify') : null

      if (site) {
        payload.siteTransition = site
      }

      return payload
    },

    // XOL-467: true when this save would SEND one of the four chain dates, i.e. whether
    // validateDates() needs to run at all (LicenseeRequest only checks the chain when a date
    // is touched).
    chainFieldsDirty () {
      var self = this

      return CHAIN_DATES.some(function (key) {
        return dateOf(self, self.telco[key]) !== self.baseline[key]
      })
    },

    onSaveModify () {
      if (this.readOnly || this.saving) return

      // 1. Validation, Telco Site ID first. A blocked save never confirms.
      var blocked = false

      if (this.baseline.telcoSiteId !== '' && textOf(this.telco.telcoSiteId).trim() === '') {
        this.telcoSiteIdError = 'Enter the Telco Site ID.'
        blocked = true
      } else {
        this.telcoSiteIdError = ''
      }

      // XOL-67: the site handover block is judged third, in the same pass as the chain (one toast, UX G2). It is judged even when no chain
      // date is dirty: the chain keeps its dirty-gate.
      var blockErrors = this.transitionCheckNow()

      if (this.chainFieldsDirty()) {
        if (!this.validateDates(blockErrors, !!this.telcoSiteIdError)) blocked = true
      } else {
        this.fieldErrors = {}

        if (this.reportBlocked([], blockErrors, !!this.telcoSiteIdError)) blocked = true
      }

      if (blocked) {
        if (this.telcoSiteIdError && Object.keys(this.fieldErrors).length === 0) this.focusTelcoSiteId(true)
        return
      }

      // 2. Nothing changed: no request, no message. Back to read mode, focus Edit.
      if (!this.isModified) {
        this.editing = false
        this.requestEditFocus()
        return
      }

      // 3. The duplicate confirm, then 4. the save.
      var that = this

      this.confirmThenRun(function () {
        that.saving = true

        // notifyFailureCallback = true is REQUIRED: without it a 403 / 409 / network failure never
        // reaches updateFailed and `saving` stays true. showMessage = false: the success message is ours.
        var payload = that.buildModifyPayload()
        that.lastSent = payload
        that.saveForm(that, '/' + LINKED_SHARER_BASE, that.actionId, payload, 'Sharer', that.updated, that.updateFailed, false, null, true)
      })
    },

    updated (data) {
      var self = this
      // Before the baselines advance: did the SHARER itself change (the first sentence of the message)?
      var sharerChanged = this.sharerModified
      // `reloadFailed`: the save COMMITTED but the API could not rebuild the row, so the body is only
      // {id, action_id}. It is never read as the row: the baselines advance from what was SENT, and step 3
      // (which gets the body through `updated`) re-reads the real row.
      var d = (data && !data.reloadFailed) ? data : {}
      var sent = this.lastSent || {}
      var raw = {}

      // Advance the baselines ONLY here, once the save landed (XOL-483): the response is the
      // decorated row; fall back to what was sent for a key it omits, else keep the baseline.
      Object.keys(this.baseline).forEach(function (key) {
        raw[key] = d[key] !== undefined ? d[key] : (sent[key] !== undefined ? sent[key] : self.baseline[key])
      })

      this.baseline = this.baselineOf(raw)
      this.lastSent = null

      // What the inputs show is now what is saved (the server may have normalised it).
      this.telco.sharerNo = this.baseline.sharerNo > 0 ? this.baseline.sharerNo : undefined
      CHAIN_DATES.forEach(function (key) { self.telco[key] = self.baseline[key] })

      this.saving = false
      this.editing = false

      var sentSite = sent.siteTransition || null

      this.applySiteTransition(d.site, sentSite)

      this.sentTransition = null
      this.resetTransition()

      this.$message({ type: 'success', message: successSentence('modify', sharerChanged, sentSite, appNowDateSql(), this.formatDate) })
      this.$emit('updated', data)

      this.requestEditFocus()
    },

    // Every modify failure leaves the form in EDIT mode with the user's values (saving cleared).
    // send() has already toasted: a coded 409 with notifyFailureCallback does NOT (PostMixins
    // 345-360), so the two 409s below say it themselves.
    updateFailed (errOrErrors) {
      this.saving = false
      this.lastSent = null

      // A completed step (XOL-596): the request drawer says why (the C-12 card), never a toast.
      // `errOrErrors` is the axios error for a refusal; for a Laravel validation 422 PostMixins
      // passes the errors MAP, which is never a step_completed.
      if (this.stepCompletedRefusal(errOrErrors, 'sharerEdit', { actionStepId: this.actionStepId })) {
        return
      }

      // XOL-67: the API refused the site handover block (the whole save: the sharer is unchanged too), or a 422 names its fields.
      if (this.transitionFailure(errOrErrors)) {
        return
      }

      var code = errorCode(errOrErrors)

      if (code === TELCO_SITE_ID_IN_USE) {
        this.telcoSiteIdError = errorMessage(errOrErrors) || 'Another sharer at this site already uses this Telco Site ID.'
        this.$notify({
          title: 'Not saved',
          message: 'The Telco Site ID needs attention. The field is marked below.',
          type: 'error',
          duration: 5500
        })

        var self = this
        this.$nextTick(function () { self.focusTelcoSiteId(true) })
        return
      }

      if (code === SHARER_TERMINATED) {
        this.$notify({
          title: 'Not saved',
          message: errorMessage(errOrErrors) || "This sharer has been terminated, so your changes can't be saved from this request.",
          type: 'warning',
          duration: 5500
        })
        // Step 3 re-reads: the form turns read-only Terminated (the user's values stay).
        this.$emit('relookup')

        var that = this
        this.$nextTick(function () { that.focusCancel() })
        return
      }

      if (code === NOT_LINKED || code === NOT_FOUND) {
        // The link is gone (PostMixins toasted the API's sentence): look again.
        this.$emit('relookup')
        return
      }

      if (code === SHARER_NO_FORBIDDEN) {
        // 403 (PostMixins toasted): the number goes back to what is saved.
        this.telco.sharerNo = this.baseline.sharerNo > 0 ? this.baseline.sharerNo : undefined
        return
      }

      // read_only_fields / invalid_value (a bug signal, toasted and logged by PostMixins), a
      // validation 422, a 500, a network error: stay in edit mode, nothing else to do.
    },

    success (response) {
      if (this.telco.cabin > 0) {
        let cabinAsset = {}
        cabinAsset.owner_id = this.telco.organisation_id
        cabinAsset.site_id = this.telco.site_id
        cabinAsset.category = 'Buildings'
        cabinAsset.type = 'Cabin'
        cabinAsset.qty = this.telco.cabin
        cabinAsset.isAuditable = true
        cabinAsset.usesPower = true
        cabinAsset.isExternal = true
        this.saveForm(this, '/assets', null, cabinAsset,  'Cabins', null, null, false)
      }


      if (this.telco.ownMeter) {
        let meterAsset = {}
        meterAsset.owner_id = this.telco.organisation_id
        meterAsset.site_id = this.telco.site_id
        meterAsset.category = 'Power'
        meterAsset.type = 'Own Meter'
        meterAsset.qty = 1
        meterAsset.isAuditable = false
        meterAsset.providesPower = true
        meterAsset.usesPower = false
        meterAsset.isExternal = true
        this.saveForm(this, '/assets', null, meterAsset, 'Own Meter', null, null, false)
      }

      // XOL-67: the extra sentence only when the block was sent and the answer carries the site (what the API wrote).
      var sent = response && response.site ? this.sentTransition : null

      this.applySiteTransition(response ? response.site : null, this.sentTransition)

      this.$message({ type: 'success', message: successSentence('create', false, sent, appNowDateSql(), this.formatDate) })

      this.sentTransition = null
      this.saving = false

      // Last: the parent flips to Modify. XOL-605 / XOL-626 (D27): when the API skipped some step-2
      // equipment rows it says so in `response.asset_warnings`; step 3 keeps them and shows an
      // in-context el-alert under the Modify meta line (not a $notify that stays until closed).
      this.$emit('created', response)
    },

    failed (response) {
      console.error('Sharer Form save failed:', response)
      this.saving = false

      // A completed step (XOL-596): the request drawer says why. `response` is the axios error
      // for the refusal (PostMixins calls this for step_completed whatever the opt-in).
      this.stepCompletedRefusal(response, 'sharer', { actionStepId: this.actionStepId })

      // XOL-67: a refusal of the site handover block (or a 422 on its fields) refused the WHOLE save: nothing was created, so there is
      // nothing for step 3 to look for. Return BEFORE `create-failed`; the form stays as the user left it.
      if (this.transitionFailure(response)) {
        return
      }

      // XOL-626 (D19): the API refused the picked rate (422 invalid_value, fields ["rate_id"]: it is no
      // longer on the request's list, e.g. unpublished since the list loaded). PostMixins toasted the
      // API's sentence. Nothing was created, so there is nothing to look for: drop the pick and load the
      // list again, and the user chooses another (or none).
      if (errorCode(response) === INVALID_VALUE && ((response.response.data || {}).fields || []).indexOf('rate_id') !== -1) {
        this.telco.rate_id = null
        this.rateSelectKey++
        return
      }

      // XOL-605: a failed Create may still have committed (a network error after the commit, a
      // double Create from another tab, 409 already_linked). Step 3 looks again; if a sharer is
      // there it flips to Modify (or Conflict), otherwise this form stays as the user left it.
      this.$emit('create-failed')
    }
  },

  computed: {
    mode () {
      return this.licensee ? 'modify' : 'create'
    },

    // XOL-626 (D6, D11): a finance or project manager, or a super, may set a billing slot.
    // isManager() short-circuits true for a super. Everyone else sees the box disabled in a
    // Modify, and (at Create) is told that only they cannot change it later.
    canEditSharerNo () {
      return this.isManager('finance|project') && !this.isViewOnly()
    },

    // XOL-626 (D19): who gets Duplicate / Modify on the Create rate select. The same gate as the
    // by-org list (api-rates.php: finance|admin|management); everyone else picks from the plain select.
    canManageRates () {
      return this.isManager('finance|admin|management')
    },

    // The request-scoped rate list (null without a request id: the select then falls back to by-org).
    rateOptionsUrl () {
      return this.actionId ? rateOptionsPath(this.actionId) : null
    },

    // Read-only (frozen / Terminated), saving, or a Modify that is not being edited.
    fieldsDisabled () {
      return this.readOnly || this.saving || (this.mode === 'modify' && !this.editing)
    },

    // XOL-626 (D27, design-system §6 "disabled = bold value"): a Modify that is not being edited,
    // or is read-only (frozen / Terminated), shows bold values, not greyed-out inputs. Create keeps
    // its controls, and so does the brief `saving` state of an edit.
    readMode () {
      return this.mode === 'modify' && (!this.editing || this.readOnly)
    },

    // The Sharer No. is a bold value in read mode, and in an edit by someone who cannot change it.
    sharerNoReadOnly () {
      return this.readMode || (this.mode === 'modify' && !this.canEditSharerNo)
    },

    // Only the live input-number reads this (the `v-else` of `sharerNoReadOnly`), so a Modify by someone
    // who cannot change the number never gets here: it renders the bold value instead.
    sharerNoDisabled () {
      return this.fieldsDisabled
    },

    // The one-line hints under the Sharer No. box (UX §3.1).
    sharerNoHints () {
      if (this.mode === 'create') {
        var hints = ['Leave blank to auto-assign the next free number.']

        if (!this.canEditSharerNo) {
          hints.push('After the sharer is created, only ' + SHARER_NO_EDITORS + ', can change it.')
        }

        return hints
      }

      if (this.editing && !this.canEditSharerNo && !this.readOnly) {
        return ['Only ' + SHARER_NO_EDITORS + ', can change the Sharer No.']
      }

      return []
    },

    // ONE descriptor for whatever fills the banner slot (UX §5.2); the first match wins.
    sharerNoBanner () {
      var common = { peers: this.peers, billingMode: this.billingMode, label: LABEL_STEP }

      if (this.mode === 'create') {
        // The billing mode is unknown at Create (no licensee yet): billingMode stays null.
        var created = noticeFor(Object.assign({}, common, { n: this.telco.sharerNo, selfId: null }))

        return created ? { kind: 'alert', title: created.title, description: created.description } : null
      }

      var editorNow = this.editing && this.canEditSharerNo && !this.readOnly

      if (editorNow) {
        if (!sharerNoDrivesFee(this.billingMode)) {
          return { kind: 'hint', text: "This rate bills by sharer quantity — the Sharer No. doesn't affect this sharer's fee." }
        }

        var notice = noticeFor(Object.assign({}, common, { n: this.telco.sharerNo, selfId: this.telco.id }))

        return notice ? { kind: 'alert', title: notice.title, description: notice.description } : null
      }

      // Read mode, read-only, or edit mode without the right to change the number: the note
      // about what is SAVED (a stored 0 is the only #0 warning step 3 can show: it cannot enter one).
      var note = noteFor(Object.assign({}, common, {
        n: this.baseline.sharerNo,
        selfId: this.telco.id,
        canEdit: this.canEditSharerNo && !this.readOnly
      }))

      return note ? { kind: 'note', lead: note.lead, detail: note.detail, peersTooltip: note.peersTooltip } : null
    },

    // The slot is a pure read (XOL-626): Tenor and Agreement Start write `telco.expiry`.
    expiryDate () {
      return this.telco.expiry
    },

    // A Modify's stored Expiry is not Agreement Start + a whole number of years (or a date is
    // blank): say so, since only Tenor can move it here.
    tenorHint () {
      if (this.mode !== 'modify' || !this.tenorMismatch) return null

      return this.editing && !this.readOnly ? TENOR_HINT_LEAD + TENOR_HINT_EDIT : TENOR_HINT_LEAD
    },

    tenorMismatch () {
      var b = this.baseline

      if (!b.started || !b.expiry) return true

      var years = this.tenorOf(b.started, b.expiry)

      return this.contractExpiryDate(b.started, years, 'years', 'YYYY-MM-DD') !== b.expiry
    },

    // XOL-67 follow-up (decisions 195, 199, 200): the site handover block. Only an RTS that an RTA's Complete opened (`fromRta`), a Candidate
    // site (Create: one with no handover date yet; Modify: any), and a site with NO other sharer that is not Terminated (an Expired one
    // counts, decision 201). The sharers list is read on mount: until it has loaded, and if it fails, the block stays hidden (fail
    // closed). Modify: edit mode only. No permission gate beyond the form's own (decision 195).
    showTransition () {
      return !!this.fromRta &&
        !this.transitionGone &&
        this.peersState === 'loaded' &&
        otherLiveSharers(this.peers, this.mode === 'modify' ? this.telco.id : null) === 0 &&
        offersTransition(this.site, this.mode) &&
        (this.mode === 'create' || (this.editing && !this.readOnly))
    },

    // After a refusal the alert stands where the block was: while the form could have shown the block, whatever the sharers list says now.
    showTransitionRefusal () {
      return !!this.fromRta && this.transitionGone && !!this.transitionRefusal &&
        (this.mode === 'create' || (this.editing && !this.readOnly))
    },

    // Is the block part of THIS save? Ticked and shown, and (Modify) not just saying what the site already says (UX A2).
    transitionSends () {
      return this.showTransition && !!this.transition && this.transition.on &&
        !(this.mode === 'modify' && sameAsSite(this.transition, this.site))
    },

    // The site's handover fields in one string: when they change under us while the block is off, its state is rebuilt from the new values.
    siteHandoverKey () {
      var s = this.site || {}

      return [s.status, s.handover, s.handoverStatus].join('|')
    },

    // The sharer's own fields (UX A5: `isModified` also counts the site handover block).
    sharerModified () {
      if (this.mode !== 'modify') return false

      var b = this.baseline
      var t = this.telco

      if (textOf(t.telcoSiteId) !== b.telcoSiteId) return true
      if (textOf(t.serviceType) !== b.serviceType) return true
      if (textOf(t.spectrumBand) !== b.spectrumBand) return true
      if (dateOf(this, t.onAir) !== b.onAir) return true
      if (numberOf(t.capex) !== b.capex) return true
      if (numberOf(t.opex) !== b.opex) return true
      if (sharerNoOf(t.sharerNo) !== b.sharerNo) return true

      return this.chainFieldsDirty()
    },

    // Any editable value differs from its baseline (null, 0 and undefined Sharer No. are one state), or the site handover block is ticked
    // and says something other than the site does.
    isModified () {
      if (this.mode !== 'modify') return false

      return this.sharerModified || this.transitionSends
    },

    // The close-time dirty check (LayerHost): editing, changed, and not mid-save.
    editDirty () {
      return this.mode === 'modify' && this.editing && !this.saving && this.isModified
    },

    editBlockedId () {
      return 'sharer-edit-blocked-' + (this.telco.id || 'x')
    }
  },

  watch: {
    // The parent needs to know (one editor at a time, the deferred refresh).
    editing (v) {
      this.$emit('editing-change', v)
    },

    editDirty (v) {
      this.drawerDirty = v
    },

    editBlocked () {
      var self = this
      this.$nextTick(function () { self.tryFocusEditOnMount() })
    },

    // XOL-67: the block's messages go the moment the sharer's date changes (the whole map: the rules are cross-field). null, '' and undefined
    // are one state: save-time normalising turns an empty date into null, and that must not wipe a message the same save just set.
    'telco.handover' (now, was) {
      var empty = function (v) { return v === null || v === undefined || v === '' }

      if (empty(now) && empty(was)) return

      this.transitionErrors = {}
    },

    siteHandoverKey () {
      if (!this.transition || !this.transition.on) {
        this.resetTransition()
      }
    }
  },

  data () {
    var lic = this.licensee
    var base = lic ? this.baselineOf(lic) : null

    return {
      format: settings.selectDateFormat,
      tenor: lic ? this.tenorOf(base.started, base.expiry) : DEFAULT_TENOR,
      utilLabel: this.getUtilLabel(),
      telco: lic ? this.telcoOf(lic) : this.getTableStruct('licensees'),
      // The loaded values, normalised, advanced only by a successful save (the XOL-467 dirty-gate).
      baseline: base || this.blankBaseline(),
      // Modify: false = read mode.
      editing: false,
      saving: false,
      formKey: 0,
      rateSelectKey: 0,
      wantEditFocus: false,
      telcoSiteIdError: '',
      lastSent: null,
      // D12: the site's sharers, and (Modify) the rate's billing mode.
      peers: [],
      billingMode: null,
      // XOL-467: any-to-all auto-fill notice flag — see maybeAutoFillDates().
      autoFilled: false,
      // XOL-67: the site handover block. Its state (siteTransition.initialState; it only matters once ticked, and is rebuilt when the site's
      // values change), its messages, the refusal that took it away for the form's life (UX A3), the body that was sent, and whether the
      // sharers list (decision 199) has loaded: 'loading' | 'loaded' | 'failed'.
      transition: transitionInitial(this.site, lic ? 'modify' : 'create', lic ? base.handover : null),
      transitionErrors: {},
      transitionGone: false,
      transitionRefusal: null,
      sentTransition: null,
      peersState: 'loading'
    }

  }, // data


  created () {
    if (this.licensee) return

    this.telco.telcoSiteId = this.sharerSiteId
    this.telco.spectrumBand = this.spectrumBand
    this.telco.serviceType = this.serviceType

    // XOL-416: prefill the Sharer No. suggestion in create mode. Clear the meta-seeded 0 first
    // so the field shows the "Auto" placeholder while the suggestion is in flight, instead of a
    // flash of "0" (which now reads as "unassigned"). XOL-626 (D16): undefined, never null (null
    // is clamped to 1 by Element's input-number when it mounts).
    this.telco.sharerNo = undefined

    // XOL-626 (D33, Carlos): the API works out the next free Sharer No. for anyone who can create
    // the sharer (the suggestion route is at POST /licensees' gate since xolas-tms-api #285), so
    // every creator gets the real number. A failed suggestion leaves `undefined`, shown as "Auto",
    // and the server assigns MAX(non-Terminated)+1 on save (D16).
    if (this.siteId) {
      this.fetchData('licensees/next-sharer-no?site_id=' + this.siteId, this.applyBillingSuggestion, 'next sharer number', this.onSuggestionFail)
    }
  },

  mounted () {
    this.loadPeers()
    this.loadBillingMode()

    if (this.licensee && this.focusEditOnMount && !this.readOnly) {
      this.requestEditFocus()
    }
  }
}

</script>

<style scoped>
/* XOL-626 (D16): wide enough for the "Unassigned" placeholder next to the right-hand controls. */
.sharer-no-input {
  width: 150px;
}

/* XOL-467: reserve room for the absolutely-positioned .el-form-item__error so
   a blocked-save message can never overlap the CAPEX/OPEX row below. Scoped
   to this custom class only (not a bare ::v-deep .el-form-item rule — that
   leaks into every nested form, XOL-451). */
.licensee-date--editing {
  margin-bottom: 56px !important;
}

.licensee-date--editing ::v-deep .el-form-item__error {
  line-height: 1.4;
}

/* XOL-626: the hints under the Sharer No. box and the Tenor slider. 12px, like the Project tab's;
   the colour is the .text-regular class on each element (readable text, never .text-muted). */
.sharer-no-hint,
.sharer-rate-hint {
  font-size: 12px;
  line-height: 1.45;
}

/* D19: the hint sits under the rate select; the form item above already leaves its own gap. */
.sharer-rate-hint--select {
  margin: -14px 0 18px;
}

.edit-blocked-hint {
  display: block;
  margin-top: 4px;
  font-size: 12px;
}

/* XOL-626 (D12): the Sharer No. banner. Copied from ProjectTab.vue (.sharer-no-banner /
   .sharer-no-note*) so the same warning reads the same on both screens. */
.sharer-no-banner {
  margin: 0 0 12px;
  max-width: 640px;
}

.sharer-no-note {
  display: flex;
  align-items: flex-start;
  max-width: 560px;
  line-height: 1.45;
}

.sharer-no-note__icon {
  flex: 0 0 auto;
  margin-right: 6px;
  font-size: 13px;
  line-height: 1.45;
  color: #F56C6C;
}

.sharer-no-note__body {
  min-width: 0; /* long organisation names wrap instead of stretching the block */
}

.sharer-no-note__lead {
  display: block;
  font-size: 13px;
  font-weight: 600;
  color: #F56C6C;
}

.sharer-no-note__detail {
  display: block;
  font-size: 12px;
  color: #606266;
}

.sharer-no-note__info {
  margin-left: 4px;
  font-size: 12px;
  color: #909399;
  cursor: help;
}
</style>
